import HttpTransport from '@/bundles/Http/transport/HttpTransport';
import ErrorMapper from '@/bundles/Http/mappers/ErrorMapper';
import { generateParams } from '@/utils/helpers';
import CompanyToMapper from '@/bundles/Companies/mappers/CompanyToMapper';
import Company from '@/bundles/Companies/models/Company';
import { Nullable } from '@/utils/types';
import { AppService } from '@/bundles/App/models/AppService';
import { ICompanyMapPinResponse } from '@/bundles/Companies/types';
import { CompanyMapPin } from '@/bundles/Companies/models/CompanyMapPin';

interface ICompanyServiceParams {
  transport: HttpTransport;
  errorMapper: ErrorMapper;
  companyMapper: CompanyToMapper;
}

const USA_CANADA_COUNTRY_CODE = '+1';

export default class CompanyService extends AppService {
  private readonly companyMapper: CompanyToMapper;

  constructor ({ transport, errorMapper, companyMapper }: ICompanyServiceParams) {
    super(transport, errorMapper);
    this.companyMapper = companyMapper;
  }

  async getCompanyByEnrich ({ params = [], controller }: { params: Array<{ name: string, value: any }>; controller?: Nullable<AbortController> }): Promise<Company | string> {
    try {
      const paramsString = generateParams(params);
      const response = await this.transport.get(`/enrich/company${paramsString}`, {}, controller);

      if (!response.success) {
        return response.error?.message || response.message;
      }

      if (response.phone) {
        response.phone = response.phone.replace(USA_CANADA_COUNTRY_CODE, ''); // Remove country code
      }

      return this.companyMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async saveComments (id, payload) {
    try {
      return await this.transport.patch(`/company/${id}/comments`, payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async saveKeywords (id, payload) {
    try {
      return await this.transport.patch(`/company/${id}/keywords`, payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getAllMapPins (
    filterParams: Record<string, unknown> = {},
    controller?: AbortController,
  ): Promise<CompanyMapPin[]> {
    try {
      const { result } = await this.transport.post('/company/list/coordinates', filterParams, controller ?? null);

      return (result as ICompanyMapPinResponse[]).map((pin, index) => new CompanyMapPin(pin, index + 1));
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }
}
